<template>
  <div class="flex flex-col gap-y-3">
    <SelectEventStatus
      v-model="event.status"
      :edition-mode="editionMode"
      :required="true"
      :errors="errors?.status"
    />

    <div class="flex w-full items-center">
      <div class="grid grid-cols-2 gap-2">
        <div class="col-span-2 flex flex-row gap-2">
          <div class="flex-1">
            <SelectDossier v-model:dossier="event.dossier" />
          </div>
          <div v-if="event.dossier_id" class="shrink-0 pt-2">
            <BaseButton
              class="flex-shrink-0"
              icon="chevron-right"
              :title="$t('button.to_dossier')"
              color="info"
              @click="
                $router.push({
                  name: 'dossier_form_information_route',
                  params: { id: event.dossier_id }
                })
              "
            />
          </div>
        </div>
        <div class="col-span-2">
          <SelectFuneralHome
            v-model="event.funeral_home_id"
            :error-key="funeral_home_id"
            :edition-mode="
              newEventIsForDossierCondition && event.dossier_id === null
            "
          />
        </div>
        <div v-if="eventTypes && !hideType" class="col-span-2">
          <SelectComponent
            v-model="event.type"
            :values="eventTypes"
            name="type"
            :label="$t('attributes.type')"
            :edition-mode="editionMode"
            :errors="errors?.type"
            searchable
            colored
            required
          />
        </div>
        <div class="col-span-2">
          <span class="text-sm font-medium capitalize-first">
            {{ $t('global.assigned_persons') }}
          </span>
          <EventUserAssignment
            v-if="!event.id"
            v-model:assigned-people="event.assignedPeopleCollection"
            v-model:assigned-people-ids="event.assignedPeople"
            :event="event"
            :edition-mode="editionMode"
            :occupations="occupations"
            :all-users-availability-items="allUsersAvailabilityItems"
            @modal-open="loadAssignmentWarnings(event)"
          />
          <EventUserSelector
            v-else
            :event="event"
            @update:event="handleAssignmentsUpdated"
          />
        </div>
        <!-- Type of visite -->
        <div
          v-if="event.type === EventType.VISIT && !hideType"
          class="col-span-2"
        >
          <InputViewerSelector
            :path="`global.event.${EventType.VISIT}.properties.visit_type`"
          >
            <BaseShowEditRadio
              :key="`visit_type_${event.id || 'new'}`"
              v-model="event.properties.visit_type"
              name="visit_type"
              :extra_id="`visit_${event.id || 'new'}`"
              :label="$t('attributes.type')"
              :options="initialMetadata.enums.visite_types"
              track-by="id"
              attribute-label="name"
              :edition-mode="editionMode"
              :translatable="false"
              :is-vertical="false"
              :errors="errors?.properties?.visit_type ?? null"
            />
          </InputViewerSelector>
        </div>
        <div class="col-span-2">
          <BaseShowEditInput
            v-model="event.title"
            :edition-mode="editionMode"
            :errors="errors.title"
            :label="$t('attributes.title')"
            name="title"
          />
        </div>
        <div
          v-if="
            [EventType.RECEPTION, EventType.REUNION, EventType.VISIT].includes(
              event.type
            )
          "
          class="col-span-2"
        >
          <BaseShowEditInput
            v-model="event.properties.nb_people"
            :edition-mode="editionMode"
            :label="$t('attributes.nb_people')"
            name="nb_people"
            :errors="errors.nb_people"
          />
        </div>
        <div v-if="event.type === EventType.RECEPTION" class="col-span-2">
          <InputViewerSelector
            :path="`global.event.${EventType.RECEPTION}.properties.reception_catering_formula`"
          >
            <BaseShowEditInput
              v-model="event.properties.reception_catering_formula"
              :edition-mode="editionMode"
              :label="$t('attributes.reception_catering_formula')"
              name="reception_catering_formula"
            />
          </InputViewerSelector>
        </div>

        <div>
          <BaseShowEditDateTimePickerv2
            v-model:time-undefined="event.date_from_undefined_time"
            v-model:date="event.date_from"
            :label-date="$t('attributes.from')"
            :edition-mode="editionMode"
            :errors="errors ? errors.date_from : null"
            :required="true"
          />
        </div>
        <div>
          <BaseShowEditDateTimePickerv2
            v-if="minDateTo"
            v-model:time-undefined="event.date_to_undefined_time"
            v-model:date="event.date_to"
            :min-date="minDateTo"
            :label-date="$t('attributes.to')"
            :edition-mode="editionMode"
            :errors="errors ? errors.date_to : null"
            :required="true"
          />
        </div>
        <div v-if="editionMode" class="col-span-2">
          <BaseShowEditCheckBoxSimple
            v-model="event.full_day"
            class="ml-10"
            name="full_day"
            :label="$t('attributes.all_day')"
            :edition-mode="editionMode"
          />
        </div>
        <div class="col-span-2">
          <PlaceInput
            v-model:place-id="event.place_id"
            v-model:place-type="event.place_kind"
            v-model:place-model="event.place_model"
            v-model:place-other="event.place_other"
            v-model:place-street="event.place_street"
            v-model:place-extra-street="event.place_extra_street"
            v-model:place-city="event.place_city"
            v-model:place-zip="event.place_zip"
            v-model:place-city-id="event.place_city_id"
            v-model:place-country="event.place_country"
            v-model:place-city-object="event.place_city_object"
            :default-place-group="'company_place'"
            :dossier-id="event.dossier_id"
            :errors="errors?.delivery_address"
            :edition-mode="editionMode"
            :label="$t(`attributes.place`)"
            :available-place-type="availablePlaceTypes"
            name="place"
          />
          <BaseShowEditInput
            v-if="['OTHER', 'RUE'].includes(event.place_kind)"
            v-model="event.place_other"
            :label="$t(`attributes.place_other`)"
            :errors="errors?.place_other"
          />
        </div>
        <div v-if="event.type === 'COC'">
          <BaseShowEditUndefinedRadioGroup
            v-model="event.properties.coffin_closing_with_sealing_process"
            name="coffin_closing_with_sealing_process"
            :label="$t('attributes.coffin_closing_with_sealing_process')"
            class="col-start-1"
            :edition-mode="true"
            :errors="
              errors
                ? errors?.properties?.coffin_closing_with_sealing_process
                : null
            "
          />
        </div>
        <div
          v-if="event.properties.coffin_closing_with_sealing_process"
          class="col-span-2 flex gap-x-1"
        >
          <BaseShowEditTimeInput
            v-model="event.properties.coffin_closing_sealing_process_time"
            :label="$t('attributes.coffin_closing_sealing_process_time')"
            :errors="
              errors.events
                ? errors.events[eventIndex]?.properties
                    ?.coffin_closing_sealing_process_time
                : null
            "
            name="coffin_closing_sealing_process_time"
            :edition-mode="true"
          />
          <BaseShowEditRadio
            v-model="event.properties.coffin_closing_sealing_process_by"
            name="coffin_closing_sealing_process_by"
            :options="[
              { id: 'FH', name: $t('attributes.funeral_home') },
              { id: 'POL', name: $t('attributes.police') }
            ]"
            :label="$t('global.by')"
            attribute-label="name"
            class="ml-4"
            :edition-mode="true"
            :errors="errors?.properties?.coffin_closing_sealing_process_by"
          />
        </div>
        <div class="col-span-2">
          <BaseShowEditTextArea
            v-model="event.remarks"
            :label="$t('attributes.remarks')"
            name="remarks"
            :errors="errors.remarks"
            :edition-mode="editionMode"
          />
        </div>
        <div class="col-span-2">
          <BaseShowEditTextArea
            v-model="event.internal_note"
            :label="$t('attributes.note_intern')"
            name="internal_note"
            :errors="errors.internal_note"
            :edition-mode="editionMode"
          />
        </div>
        <div v-if="planningMode" class="col-span-2">
          <BaseShowEditTextArea
            v-model="event.internal_note_planning"
            :label="`${$t('attributes.note_intern')} (${$t(
              'global.planning'
            )})`"
            name="internal_note_planning"
            :errors="errors.internal_note_planning"
            :edition-mode="editionMode"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { EventType, PlaceType } from '@/assets/enums'
import PlaceInput from '@c/addf-package/components/Place/PlaceInput.vue'
import BaseShowEditDateTimePickerv2 from '@c/addf-package/components/BaseShowEditDatePicker/BaseShowEditDateTimePickerv2.vue'
import BaseShowEditInput from '@c/addf-package/components/BaseShowEditInput/BaseShowEditInput.vue'
import SelectEventStatus from '@c/InitialStateFormComponent/Enums/SelectEventStatus.vue'
import moment from 'moment'
import BaseShowEditTextArea from '@c/addf-package/components/BaseShowEditInput/BaseShowEditTextArea.vue'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import SelectFuneralHome from '@c/InitialStateFormComponent/SelectFuneralHome.vue'
import SelectDossier from '@c/SelectAjaxComponent/SelectDossier.vue'
import BaseShowEditCheckBoxSimple from '@c/BaseShowEditCheckBoxSimple.vue'
import SelectComponent from '@c/BaseFormComponent/SelectComponent.vue'
import BaseShowEditUndefinedRadioGroup from '@c/addf-package/components/BaseRadioGroup/BaseShowEditUndefinedRadioGroup.vue'
import BaseShowEditTimeInput from '@c/addf-package/components/BaseShowEditInput/BaseShowEditTimeInput.vue'
import BaseShowEditRadio from '@c/addf-package/components/BaseShowEditRadioGroup/BaseShowEditRadio.vue'
import InputViewerSelector from '@c/InputViewerSelector.vue'
import EventUserAssignment from '@c/Event/EventUserAssignment.vue'
import EventUserSelector from '@c/Event/EventUserSelector.vue'
import AssignmentWarningsMixin from '@u/mixins/Event/AssignmentWarningsMixin'
import {
  eventTypesWithoutDossier,
  typeAfterDossierRemoved
} from '@ui/modal/event/agendaEventTypes'

export default {
  name: 'EditEventModal',
  mixins: [AssignmentWarningsMixin],
  components: {
    InputViewerSelector,
    EventUserAssignment,
    EventUserSelector,
    BaseShowEditRadio,
    BaseShowEditTimeInput,
    BaseShowEditUndefinedRadioGroup,
    SelectComponent,
    BaseShowEditTextArea,
    PlaceInput,
    BaseShowEditCheckBoxSimple,
    BaseShowEditDateTimePickerv2,
    BaseShowEditInput,
    SelectFuneralHome,
    BaseButton,
    SelectDossier,
    SelectEventStatus
  },
  props: {
    editionMode: {
      type: Boolean,
      required: true
    },
    newEventDossier: {
      type: Object,
      required: false,
      default: null
    },
    hideType: {
      type: Boolean,
      required: false,
      default: false
    },
    planningMode: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  emits: ['address'],
  data() {
    return {
      EventType: EventType,
      eventTypes: []
    }
  },
  computed: {
    ...mapGetters({
      event: 'agenda/getEvent',
      errors: 'auth/getErrors',
      initialMetadata: 'metadata/getInitialMetadata'
    }),
    newEventIsForDossierCondition() {
      if (this.newEventDossier) return false
      return this.editionMode
    },
    typeCondition() {
      if (this.newEventDossier || this.event.id) return false
      return this.event.dossier_id !== null && this.editionMode
    },
    dossierDefaultValue() {
      if (!this.event || !this.event.dossier) return {}
      return {
        id: this.event.dossier_id ?? null,
        title: this.event.dossier.title
      }
    },
    minDateTo() {
      return moment(this.event.date_from)?.format('YYYY-MM-DD HH:mm')
    },
    availablePlaceTypes() {
      switch (this.event.type) {
        case EventType.VISIT:
          return this.initialMetadata.settings.visit_place_type
        case EventType.BODY_RAISING:
          return this.initialMetadata.settings.body_raising_place_types
        case EventType.BURIAL:
          return this.initialMetadata.settings.burial_place_types
        case EventType.CEREMONY:
          return this.initialMetadata.settings.ceremony_place_types
        case EventType.COFFIN_CLOSING:
          return this.initialMetadata.settings.coffin_closing_place_type
        case EventType.COFFIN_PLACING:
          return this.initialMetadata.settings.coffin_placing_place_type
        case EventType.REUNION:
          return this.initialMetadata.settings.reunion_place_type
        case EventType.THANATO:
          return this.initialMetadata.settings.thanato_place_type
        case EventType.WAKE:
          return this.initialMetadata.settings.wake_place_types
        default:
          return Object.values(PlaceType)
      }
    }
  },
  watch: {
    'event.dossier'(newValue, oldValue) {
      if (newValue) {
        this.event.dossier_id = newValue.id
        this.afterDossierSelected(newValue)
      } else {
        this.event.dossier_id = null
        this.eventTypes = eventTypesWithoutDossier(
          this.initialMetadata?.enums?.event_type_list
        )
        this.event.type = typeAfterDossierRemoved(this.event.type)
      }
    },
    'event.type'(newValue, oldValue) {
      if (!newValue || newValue === oldValue) return
      this.applyDefaultDuration(newValue)
      this.applyDefaultTitle(newValue)
    }
  },
  async mounted() {
    this.eventTypes = eventTypesWithoutDossier(
      this.initialMetadata?.enums?.event_type_list
    )
    if (this.event.dossier?.id) {
      await this.getAvailableEventTypes(this.event.dossier)
    }

    if (
      this.event.type &&
      !this.eventTypes.find((e) => e.id === this.event.type)
    ) {
      const currentType = this.initialMetadata?.enums?.event_type_list.find(
        (e) => e.id === this.event.type
      )
      if (currentType) {
        this.eventTypes.push(currentType)
      }
    }

    if (this.event?.date_to) {
      this.event.date_to_undefined_time = false
    }

    if (this.event.type) {
      this.applyDefaultDuration(this.event.type)
    }
  },
  methods: {
    ...mapMutations({
      processSetEventDateFromMutation: 'agenda/setEventDateFrom',
      processSetEventDateToMutation: 'agenda/setEventDateTo'
    }),
    ...mapActions({
      processGetAvailableEventTypeByDossierAction:
        'dossier/getAvailableEventTypeOfDossier'
    }),
    handleAssignmentsUpdated(updatedEvent) {
      this.event.assignedPeople = updatedEvent.assignedPeople
      this.event.assignedPeopleCollection =
        updatedEvent.assignedPeopleCollection
      this.event.materialResources = updatedEvent.materialResources
      this.event.materialResourcesCollection =
        updatedEvent.materialResourcesCollection
    },
    getDefaultDurationForType(typeId) {
      const typeKey = Object.keys(EventType).find(
        (key) => EventType[key] === typeId
      )
      return (
        this.initialMetadata?.settings?.[
          typeKey?.toLowerCase() + '_default_duration'
        ] ?? null
      )
    },
    // Recalculate date_to based on global settings when type changes on a new event
    applyDefaultDuration(type) {
      if (this.event.id || !this.event.date_from) return

      const duration = this.getDefaultDurationForType(type)

      if (!duration) return

      // Keep a duration already selected in the agenda (e.g. by dragging a slot) when it
      // already meets or exceeds the default duration; only extend it up to the default
      // when it is shorter (BBSW-6017).
      if (this.event.date_to) {
        const selectedDuration = moment(this.event.date_to).diff(
          moment(this.event.date_from),
          'minutes'
        )
        if (selectedDuration >= duration) return
      }

      this.event.date_to = moment(this.event.date_from)
        .add(duration, 'minutes')
        .format('YYYY-MM-DD HH:mm')
      this.event.date_to_undefined_time = false
    },
    // Update event title based on type and linked dossier
    applyDefaultTitle(type) {
      if (this.event.id || !this.event.dossier) return

      if (type && type !== EventType.OTHER) {
        const eventType = this.initialMetadata?.enums?.event_type_list.find(
          (elt) => elt.id === type
        )
        this.event.title =
          eventType?.name + ' - ' + this.event.dossier?.title?.split(' - ')[1]
      } else {
        this.event.title = ''
      }
    },
    async getAvailableEventTypes(dossier) {
      if (dossier?.id) {
        const response = await this.processGetAvailableEventTypeByDossierAction(
          {
            dossier_id: dossier.id
          }
        )

        this.eventTypes = this.initialMetadata?.enums?.event_type_list?.filter(
          (type) => response.types.includes(type.id)
        )
      } else {
        this.eventTypes = eventTypesWithoutDossier(
          this.initialMetadata?.enums?.event_type_list
        )
      }
    },
    async afterDossierSelected(dossier) {
      if (this.event.funeral_home_id !== dossier?.funeral_home_id) {
        this.event.funeral_home_id = dossier?.funeral_home_id ?? null
        // this.event.place_type = null
        // this.event.place_id = null
        // this.event.place_other = null
        // this.event.place_model = null
      }

      this.event.dossier_id = dossier?.id ?? null
      this.event.dossier.title = dossier?.title ?? null
      this.event.dossier.death_date_time = dossier?.death_date_time ?? null
      this.event.type = this.event.type ?? EventType.OTHER

      if (!this.event.id && dossier?.id) {
        this.getAvailableEventTypes(dossier)
      }
    }
  }
}
</script>
